import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const ADDED_FLASH_MS = 1400;
const CLOSE_GRACE_MS = 400;

export class QuickAddComponent extends Component {
  /** @type {number | undefined} */
  #flashTimeout;

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#flashTimeout) clearTimeout(this.#flashTimeout);
  }

  get productTitle() {
    return this.dataset.productTitle || '';
  }

  get optionName() {
    return this.dataset.optionName || '';
  }

  get productHandle() {
    return this.dataset.productHandle || '';
  }

  /** @returns {HTMLButtonElement[]} */
  get chips() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.yg-size-bar__chip')]);
  }

  /**
   * @param {string | undefined} variantId
   * @returns {boolean}
   */
  addVariant(variantId) {
    if (!variantId) return false;

    const form = this.querySelector('form');
    const input = form?.querySelector('input[name="id"]');

    if (!form || !(input instanceof HTMLInputElement)) return false;

    input.disabled = false;
    input.value = variantId;
    form.requestSubmit();

    return true;
  }

  /** @param {Event} event */
  handleSizeClick = (event) => {
    const chip = event.target;

    if (!(chip instanceof HTMLButtonElement) || chip.disabled) return;
    if (!this.addVariant(chip.dataset.variantId)) return;

    this.flashAdded(chip);
  };

  /** @param {Event} event */
  handleTriggerClick = (event) => {
    event.preventDefault();

    const sheet = document.getElementById('yg-size-sheet');

    if (sheet instanceof YgSizeSheet && event.target instanceof HTMLElement) {
      sheet.openFor(this, event.target);
    }
  };

  /** @param {HTMLElement} chip */
  flashAdded(chip) {
    if (this.#flashTimeout) clearTimeout(this.#flashTimeout);

    for (const other of this.chips) other.classList.remove('is-added');
    chip.classList.add('is-added');

    this.#flashTimeout = setTimeout(() => chip.classList.remove('is-added'), ADDED_FLASH_MS);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class YgSizeSheet extends DialogComponent {
  requiredRefs = ['dialog', 'title', 'label', 'sizes', 'addButton', 'chartButton'];

  /** @type {QuickAddComponent | null} */
  #source = null;
  /** @type {HTMLElement | null} */
  #trigger = null;
  /** @type {string | null} */
  #selected = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.sizes.addEventListener('click', this.#handleSelect);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.sizes.removeEventListener('click', this.#handleSelect);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
  }

  /**
   * @param {QuickAddComponent} source
   * @param {HTMLElement} trigger
   */
  openFor(source, trigger) {
    const { title, label, sizes, addButton, chartButton } = this.refs;

    this.#source = source;
    this.#trigger = trigger;
    this.#selected = null;

    title.textContent = source.productTitle;
    label.textContent = source.optionName;

    sizes.replaceChildren(...source.chips.map(toSheetChip));
    addButton.disabled = true;

    chartButton.hidden = true;
    loadSizeChart(source.productHandle).then((chart) => {
      if (chart && this.#source === source) chartButton.hidden = false;
    });

    this.showDialog();
  }

  /* size sheet — size chart link */
  handleSizeChart = async () => {
    const source = this.#source;
    const trigger = this.#trigger;
    const selected = this.#selected;

    if (!source) return;

    await Promise.race([this.closeDialog(), new Promise((resolve) => setTimeout(resolve, CLOSE_GRACE_MS))]);

    const modal = await openSizeChart(source.productHandle);

    if (!modal) {
      this.#reopen(source, trigger, selected);
      return;
    }

    const observer = new MutationObserver(() => {
      if (modal.style.visibility !== 'hidden') return;

      observer.disconnect();
      setTimeout(() => this.#reopen(source, trigger, selected), CLOSE_GRACE_MS);
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['style'] });
  };

  /**
   * @param {QuickAddComponent} source
   * @param {HTMLElement | null} trigger
   * @param {string | null} selected
   */
  #reopen(source, trigger, selected) {
    if (!source.isConnected || this.refs.dialog.open) return;

    this.openFor(source, trigger ?? source);

    const chip = [...this.refs.sizes.children].find(
      (child) => child instanceof HTMLElement && child.dataset.variantId === selected
    );

    if (chip instanceof HTMLButtonElement) chip.click();
  }

  /** @param {Event} event */
  #handleSelect = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const chip = target.closest('.yg-size-sheet__chip');

    if (!(chip instanceof HTMLButtonElement) || chip.disabled) return;

    for (const other of this.refs.sizes.children) {
      other.classList.toggle('is-selected', other === chip);
      other.setAttribute('aria-pressed', String(other === chip));
    }

    this.#selected = chip.dataset.variantId ?? null;
    this.refs.addButton.disabled = this.#selected == null;
  };

  /* size sheet — close before adding, never overlap the drawer's scroll lock.
     closeDialog waits on the close animation, which never finishes while the
     tab is hidden, so the add is never allowed to wait on it indefinitely. */
  handleAdd = async () => {
    const source = this.#source;
    const variantId = this.#selected;

    if (!source || !variantId) return;

    await Promise.race([this.closeDialog(), new Promise((resolve) => setTimeout(resolve, CLOSE_GRACE_MS))]);

    source.addVariant(variantId);
  };

  #restoreFocus = () => {
    this.#trigger?.focus();
    this.#trigger = null;
  };
}

/**
 * @param {HTMLButtonElement} chip
 * @returns {HTMLButtonElement}
 */
function toSheetChip(chip) {
  const copy = /** @type {HTMLButtonElement} */ (chip.cloneNode(true));

  copy.className = 'yg-size-sheet__chip';
  copy.removeAttribute('on:click');
  copy.removeAttribute('aria-label');
  copy.setAttribute('aria-pressed', 'false');

  return copy;
}

/* size sheet — bf-size-charts app, booted per product from its own PDP block */
const SIZE_CHART_VIEW = 'yg-size-chart';
const SIZE_CHART_JQUERY = 'https://size-charts-relentless.herokuapp.com/js/jquery-3.6.0.min.js';
const SIZE_CHART_BOOT_MS = 5000;

/** @type {Map<string, Promise<{ data: string, src: string } | null>>} */
const sizeCharts = new Map();
const pageHandle = window.sizeChartsRelentless?.product?.handle ?? null;
let bootedHandle = pageHandle;
/** @type {Promise<boolean>} */
let bootQueue = Promise.resolve(true);

/**
 * @param {string} handle
 * @returns {Promise<{ data: string, src: string } | null>}
 */
function loadSizeChart(handle) {
  if (!handle) return Promise.resolve(null);

  let chart = sizeCharts.get(handle);

  if (!chart) {
    const url = new URL(`${window.Shopify?.routes?.root ?? '/'}products/${handle}`, location.origin);
    url.searchParams.set('view', SIZE_CHART_VIEW);

    chart = fetch(url)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return null;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const scripts = [...doc.querySelectorAll('.shopify-app-block script')];
        const data = scripts.find(
          (script) => !script.hasAttribute('src') && script.textContent?.includes('sizeChartsRelentless.metafield.chart =')
        )?.textContent;
        const src = scripts.find((script) => script.getAttribute('src')?.includes('size-charts-relentless'))?.getAttribute('src');

        return data && src ? { data, src } : null;
      })
      .catch(() => null);

    sizeCharts.set(handle, chart);
  }

  return chart;
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.src = src;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.append(script);
  });
}

/**
 * @param {string} handle
 * @returns {Promise<boolean>}
 */
async function bootSizeChart(handle) {
  if (bootedHandle === handle && document.querySelector('.scr-modal')) return true;

  const chart = await loadSizeChart(handle);

  if (!chart) return false;

  if (!window.jQuery) await loadScript(SIZE_CHART_JQUERY);

  for (const element of document.querySelectorAll('.scr-modal, .scr-shadow')) element.remove();

  const data = document.createElement('script');
  data.textContent = chart.data;
  document.head.append(data);
  data.remove();

  const app = window.sizeChartsRelentless;
  const booted = new Promise((resolve) => {
    app.done = () => resolve(true);
    setTimeout(() => resolve(false), SIZE_CHART_BOOT_MS);
  });

  app.ran = false;
  await loadScript(chart.src);

  const ok = await booted;

  if (ok) bootedHandle = handle;

  return ok;
}

/**
 * @param {string} handle
 * @returns {Promise<boolean>}
 */
function queueSizeChartBoot(handle) {
  bootQueue = bootQueue.catch(() => false).then(() => bootSizeChart(handle));

  return bootQueue;
}

/**
 * @param {string} handle
 * @returns {Promise<HTMLElement | null>}
 */
async function openSizeChart(handle) {
  if (!(await queueSizeChartBoot(handle).catch(() => false))) return null;

  const opener = document.querySelector('.yg-size-sheet__chart-host .scr-open-size-chart');
  const modal = document.querySelector('.scr-modal');

  if (!(opener instanceof HTMLElement) || !(modal instanceof HTMLElement)) return null;

  opener.click();

  return modal;
}

/* product page — its own size guide button re-boots the page product after a card chart */
if (pageHandle) {
  for (const type of ['click', 'touchend']) {
    document.addEventListener(
      type,
      (event) => {
        const opener = event.target instanceof Element ? event.target.closest('.scr-open-size-chart') : null;

        if (!(opener instanceof HTMLElement) || opener.closest('.yg-size-sheet__chart-host')) return;
        if (bootedHandle === pageHandle) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        queueSizeChartBoot(pageHandle).then((ok) => ok && opener.click());
      },
      true
    );
  }
}

if (!customElements.get('yg-size-sheet')) {
  customElements.define('yg-size-sheet', YgSizeSheet);
}
